import React, { useCallback, useState } from "react";

import { AccordionHeader, Button, Text } from "@fluentui/react-components";
import {
  ArrowStepBackRegular,
  DeleteRegular,
  DocumentRegular,
  DocumentTextRegular,
} from "@fluentui/react-icons";
import { devices } from "library/constants";
import { fileExtensionIcons } from "library/shared";
import { IAttachment, IFormattedFile } from "library/types";
import { formatDateString, genUUID, getExtensionFromUrl, openLinkInNewTab } from "library/utils";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";

type Props = {
  attachment: IAttachment;
  openAttachForms: string[];
  attachmentActionsState: {
    createAttachmentInProgress: boolean;
    deleteInProgress: boolean;
    onCreateAttachment: (attach: IFormattedFile | null, isHiddenForEmployee?: boolean) => void;
    onDeleteAttachment: (attach: IAttachment) => void;
  };
};

export function AttachmentHeader({ attachment, attachmentActionsState, openAttachForms }: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const docType = getExtensionFromUrl(attachment.attachmentPath);

  const toggleDeleteConfirmationState = useCallback(() => {
    setDeleteConfirmationOpen((prev) => !prev);
  }, []);

  const { deleteInProgress, onDeleteAttachment } = attachmentActionsState;

  return (
    <>
      <AccordionHeader size="large" expandIconPosition="end" expandIcon={null}>
        <Row className="row-item">
          <Flex $justify="space-between">
            <Button
              data-help="profile-document-open"
              style={{
                // color: theme.palette.purpleDark,
                textDecoration: "underline",
                paddingLeft: 0,
                justifyContent: "flex-start",
                minWidth: "fit-content",
                maxWidth: "fit-content",
              }}
              href={attachment?.attachmentPath || ""}
              onClick={(e) => {
                e.stopPropagation();
                openLinkInNewTab(attachment?.attachmentPath || "");
              }}
              appearance="transparent"
              icon={fileExtensionIcons[docType] || <DocumentRegular />}
            >
              <Text truncate>{attachment.attachmentName}</Text>
            </Button>
          </Flex>
          <Text>{formatDateString(attachment?.lastModifiedAt || "")}</Text>

          <Flex
            wrap="nowrap"
            $gap={"0px"}
            style={{
              marginLeft: "auto",
            }}
          >
            <Button
              data-help="profile-document-details"
              size="small"
              appearance="subtle"
              style={{
                color: theme.palette.purpleDark,
              }}
              icon={
                openAttachForms.includes(attachment?.attachmentName || "") ? (
                  <ArrowStepBackRegular />
                ) : (
                  <DocumentTextRegular />
                )
              }
            />
            <Button
              data-help="profile-document-delete"
              style={{
                color: theme.palette.purpleDark,
              }}
              appearance="transparent"
              disabled={deleteInProgress}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmationOpen(true);
              }}
              icon={<DeleteRegular />}
            ></Button>
          </Flex>
        </Row>
      </AccordionHeader>

      {deleteConfirmationOpen && (
        <ConfirmationDialog
          key={genUUID()}
          isOpen={deleteConfirmationOpen}
          toggleOpen={toggleDeleteConfirmationState}
          onConfirm={() => {
            onDeleteAttachment(attachment);
            toggleDeleteConfirmationState();
          }}
          description={t("hbh.deleteDocumentConfirm.label")}
          confirmLabel={t("greco.delete")}
          title={`${t("hbh.deleteDocument.label")}: ${attachment.attachmentName} `}
        />
      )}
    </>
  );
}

const Row = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  width: 100%;
  max-width: 800px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  padding: 10px;
  align-items: center;
  box-sizing: border-box;
  @media only screen and ${devices.md} {
    grid-template-columns: 6fr 1fr 1fr;
  }
`;
